import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  History,
  Search,
  X,
  AlertCircle,
  ArrowUpRight,
  RefreshCw,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import {
  fetchToolAccess,
  selectToolAccessInvocations,
  selectToolAccessRangeKey,
  selectToolAccessStatus,
  setSelectedTaskId,
} from '../../../features/openai/openaiSlice'
import { formatCredits } from '../../../sharedComponents/AIChatBox/MessageCreditCost'
import { changeApplication } from 'actions/rootActions'
import { connect } from 'react-redux'

const humanLabel = (toolName: string): string => {
  const labels: Record<string, string> = {
    search: 'Search',
    fetch: 'Fetch Data',
    get_traffic_data: 'Traffic Data',
    get_traffic_metrics: 'Traffic Metrics',
    get_traffic_summary: 'Traffic Summary',
    get_ads_performance: 'Ads Performance',
    insure_shipment: 'Insure Shipment',
    void_insurance: 'Void Insurance',
    update_shipping_address: 'Update Address',
    get_current_shipping_settings: 'Shipping Settings',
    export_to_email: 'Email Export',
    export_to_google_drive: 'Google Drive Export',
    export_to_csv: 'CSV Download',
    modify_google_ads: 'Update Google Ads',
    search_google_ads: 'Search Google Ads',
    google_web_search: 'Google Search',
    modify_facebook_ads: 'Update Facebook Ads',
    search_facebook_ads: 'Search Facebook Ads',
    get_orders_report: 'Orders Report',
    get_line_items_report: 'Line Items Report',
    get_charts_report: 'Charts Report',
    get_trends_report: 'Trends Report',
    get_year_comparison_report: 'Year Comparison',
    get_customers_report: 'Customers Report',
    get_products_report: 'Products Report',
    get_variants_report: 'Variants Report',
    get_brands_report: 'Brands Report',
    get_skus_report: 'SKU Report',
    get_marketing_report: 'Marketing Report',
    get_integrated_accounts: 'Connected Accounts',
    get_mmm_summary: 'MMM Summary',
    run_mmm_simulation: 'MMM Simulation',
    fetch_webpage: 'Fetch Webpage',
  }
  return labels[toolName] || toolName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function formatTimestamp(ts: number): { date: string; time: string } {
  if (!ts) return { date: '—', time: '' }
  const d = new Date(ts)
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  const time = d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  return { date, time }
}

/** List-price USD from credits ($0.50/credit). Prefer server `usd` when present. */
function formatUsd(usd: number | null | undefined, credits?: number | null): string {
  let n = typeof usd === 'number' && Number.isFinite(usd) ? usd : null
  if (n == null && typeof credits === 'number' && Number.isFinite(credits)) {
    n = credits / 2
  }
  if (n == null || !Number.isFinite(n) || n <= 0) return ''
  return n < 0.01
    ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 4 })}`
    : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

type StatusFilter = 'all' | 'completed' | 'failed'
type WriteFilter = 'all' | 'read' | 'write'
type SourceFilter = 'all' | 'ai' | 'mcp' | 'scheduled'
type RangeKey = '7d' | '30d' | 'mtd' | 'all'

const SOURCE_FILTER_OPTIONS: { key: SourceFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'ai', label: 'AI' },
  { key: 'mcp', label: 'MCP' },
  { key: 'scheduled', label: 'Scheduled' },
]

type Invocation = {
  chatId: string
  chatTitle: string
  turn: number
  callIndex?: number | null
  tool: string
  status: 'completed' | 'failed'
  isWrite: boolean
  timestamp: number
  tokens: number | null
  source?: 'chat' | 'mcp'
  xSource?: string | null
  credits?: number | null
  usd?: number | null
  billingKind?: 'tool' | 'included_in_run' | 'run_share_estimate' | null
  sessionCredits?: number | null
  sessionUsd?: number | null
}

type SessionGroup = {
  chatId: string
  chatTitle: string
  fromMcp: boolean
  calls: Invocation[]
  callCount: number
  okCount: number
  failedCount: number
  readCount: number
  writeCount: number
  creditsTotal: number
  usdTotal: number
  sessionCredits: number | null
  sessionUsd: number | null
  hasRunShareEstimates: boolean
  latestTs: number
}

const RANGE_OPTIONS: { key: RangeKey; label: string }[] = [
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: 'mtd', label: 'MTD' },
  { key: 'all', label: '90d' },
]

function rangeParams(rangeKey: RangeKey): { from?: string; to?: string; rangeKey: RangeKey } {
  const now = new Date()
  const to = now.toISOString()
  if (rangeKey === 'all') {
    const from = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString()
    return { from, to, rangeKey }
  }
  if (rangeKey === 'mtd') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
    return { from, to, rangeKey }
  }
  const days = rangeKey === '7d' ? 7 : 30
  const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString()
  return { from, to, rangeKey }
}

function formatCreditCell(inv: Invocation): {
  text: string
  usdText: string
  title?: string
  estimateLabel?: string
} {
  if (inv.billingKind === 'run_share_estimate') {
    if (typeof inv.credits === 'number' && inv.credits > 0) {
      // List-price UX fallback uses 0.10 / 0.01 / 0.25; ledger equal-split is other values.
      const looksLikeList =
        inv.credits === 0.1 || inv.credits === 0.01 || inv.credits === 0.25
      return {
        text: `~${formatCredits(inv.credits)}`,
        usdText: inv.usd != null && inv.usd > 0 ? `~${formatUsd(inv.usd, inv.credits)}` : '',
        title: looksLikeList
          ? 'Rough estimate from MCP list prices (not the scheduled-run ledger charge).'
          : 'Estimate: equal share of the scheduled-task run charge (not a per-tool bill). The run is billed once.',
        estimateLabel: looksLikeList ? 'est. list' : 'est. split',
      }
    }
    return {
      text: '—',
      usdText: '',
      title: 'Billed on scheduled task / widget run',
    }
  }
  if (inv.billingKind === 'included_in_run') {
    // Frontend safety net if API still returns bare included_in_run
    const est = inv.status === 'failed' ? 0.01 : 0.1
    return {
      text: `~${formatCredits(est)}`,
      usdText: `~${formatUsd(null, est)}`,
      title:
        'Rough estimate from MCP list prices. Scheduled runs bill once via scheduled_task_agent when ledger rows are available.',
      estimateLabel: 'est. list',
    }
  }
  if (typeof inv.credits === 'number' && inv.credits > 0) {
    return {
      text: formatCredits(inv.credits),
      usdText: formatUsd(inv.usd, inv.credits),
    }
  }
  return { text: '—', usdText: '' }
}

function isMcpInvocation(inv: Invocation) {
  return inv.source === 'mcp' || (typeof inv.chatId === 'string' && inv.chatId.startsWith('mcp_'))
}

function isScheduledInvocation(inv: Invocation) {
  if (!isMcpInvocation(inv)) return false
  const x = typeof inv.xSource === 'string' ? inv.xSource.trim() : ''
  if (x === 'payhelm-scheduled-task' || /payhelm-scheduled-task/i.test(x)) return true
  const title = inv.chatTitle || ''
  return /scheduled\s+task/i.test(title) || /payhelm-scheduled-task/i.test(title)
}

function ToolAccessMainViewComponent({ currentApp, changeApplication: changeApp }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const invocations: Invocation[] = useSelector(selectToolAccessInvocations)
  const rangeKey: RangeKey = useSelector(selectToolAccessRangeKey)
  const status = useSelector(selectToolAccessStatus)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [writeFilter, setWriteFilter] = useState<WriteFilter>('all')
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all')
  const [chatIdFilter, setChatIdFilter] = useState<string | null>(() => searchParams.get('chatId'))
  const [expandedById, setExpandedById] = useState<Record<string, boolean>>({})

  useEffect(() => {
    const id = searchParams.get('chatId')
    setChatIdFilter(id)
  }, [searchParams])

  const clearChatIdFilter = () => {
    setChatIdFilter(null)
    const next = new URLSearchParams(searchParams)
    next.delete('chatId')
    setSearchParams(next, { replace: true })
  }

  const loadRange = useCallback(
    (key: RangeKey) => {
      dispatch(fetchToolAccess(rangeParams(key)) as any)
    },
    [dispatch]
  )

  useEffect(() => {
    if (status === 'idle') {
      loadRange('30d')
    }
  }, [status, loadRange])

  const filtered = useMemo(() => {
    return invocations.filter(inv => {
      if (chatIdFilter && inv.chatId !== chatIdFilter) return false
      if (statusFilter !== 'all' && inv.status !== statusFilter) return false
      if (writeFilter === 'read' && inv.isWrite) return false
      if (writeFilter === 'write' && !inv.isWrite) return false
      if (sourceFilter !== 'all') {
        const fromMcp = isMcpInvocation(inv)
        const fromScheduled = isScheduledInvocation(inv)
        if (sourceFilter === 'ai' && fromMcp) return false
        if (sourceFilter === 'mcp' && (!fromMcp || fromScheduled)) return false
        if (sourceFilter === 'scheduled' && !fromScheduled) return false
      }
      if (search) {
        const q = search.toLowerCase()
        if (
          !inv.tool.includes(q) &&
          !humanLabel(inv.tool).toLowerCase().includes(q) &&
          !(inv.chatTitle || '').toLowerCase().includes(q)
        ) {
          return false
        }
      }
      return true
    })
  }, [invocations, statusFilter, writeFilter, sourceFilter, search, chatIdFilter])

  const sessions: SessionGroup[] = useMemo(() => {
    const map = new Map<string, SessionGroup>()
    for (const inv of filtered) {
      let group = map.get(inv.chatId)
      if (!group) {
        group = {
          chatId: inv.chatId,
          chatTitle: inv.chatTitle || inv.chatId,
          fromMcp: isMcpInvocation(inv),
          calls: [],
          callCount: 0,
          okCount: 0,
          failedCount: 0,
          readCount: 0,
          writeCount: 0,
          creditsTotal: 0,
          usdTotal: 0,
          sessionCredits: null,
          sessionUsd: null,
          hasRunShareEstimates: false,
          latestTs: 0,
        }
        map.set(inv.chatId, group)
      }
      group.calls.push(inv)
      group.callCount += 1
      if (inv.status === 'failed') group.failedCount += 1
      else group.okCount += 1
      if (inv.isWrite) group.writeCount += 1
      else group.readCount += 1
      if (inv.billingKind === 'run_share_estimate') group.hasRunShareEstimates = true
      const c = Number(inv.credits)
      if (Number.isFinite(c) && c > 0) {
        group.creditsTotal += c
        const u = typeof inv.usd === 'number' && Number.isFinite(inv.usd) ? inv.usd : c / 2
        group.usdTotal += u
      }
      if (
        group.sessionCredits == null &&
        typeof inv.sessionCredits === 'number' &&
        Number.isFinite(inv.sessionCredits)
      ) {
        group.sessionCredits = inv.sessionCredits
        group.sessionUsd =
          typeof inv.sessionUsd === 'number' && Number.isFinite(inv.sessionUsd)
            ? inv.sessionUsd
            : inv.sessionCredits / 2
      }
      if (inv.timestamp > group.latestTs) group.latestTs = inv.timestamp
      if (!group.chatTitle && inv.chatTitle) group.chatTitle = inv.chatTitle
    }
    const list = Array.from(map.values())
    for (const g of list) {
      g.calls.sort((a, b) => b.timestamp - a.timestamp)
      // Session header estimate when ledger attribution left sessionCredits empty
      if (
        (g.sessionCredits == null || g.sessionCredits <= 0) &&
        g.calls.some((c) => isScheduledInvocation(c))
      ) {
        let est = 0
        for (const c of g.calls) {
          if (typeof c.credits === 'number' && c.credits > 0) est += c.credits
          else if (c.billingKind === 'included_in_run' || c.billingKind === 'run_share_estimate') {
            est += c.status === 'failed' ? 0.01 : 0.1
          }
        }
        if (est > 0) {
          g.sessionCredits = Math.round(est * 1e4) / 1e4
          g.sessionUsd = g.sessionCredits / 2
          g.hasRunShareEstimates = true
        }
      }
    }
    list.sort((a, b) => b.latestTs - a.latestTs)
    return list
  }, [filtered])

  const isExpanded = (group: SessionGroup) => expandedById[group.chatId] === true

  const toggleExpanded = (chatId: string, currentlyExpanded: boolean) => {
    setExpandedById(prev => ({ ...prev, [chatId]: !currentlyExpanded }))
  }

  const rowCreditsTotal = useMemo(() => {
    const total = sessions.reduce((sum, g) => {
      if (g.sessionCredits != null && g.sessionCredits > 0) return sum + g.sessionCredits
      if (g.creditsTotal > 0) return sum + g.creditsTotal
      return sum
    }, 0)
    return Math.round(total * 1e4) / 1e4
  }, [sessions])

  const rowUsdTotal = useMemo(() => {
    const total = sessions.reduce((sum, g) => {
      if (g.sessionCredits != null && g.sessionCredits > 0) {
        return sum + (g.sessionUsd != null && g.sessionUsd > 0 ? g.sessionUsd : g.sessionCredits / 2)
      }
      if (g.creditsTotal > 0) return sum + g.usdTotal
      return sum
    }, 0)
    return Math.round(total * 1e4) / 1e4
  }, [sessions])

  const handleChatClick = (chatId: string) => {
    changeApp(currentApp, 'ai')
    dispatch(setSelectedTaskId(null))
    navigate('/ai', { state: { selectedChat: { message_id: chatId } } })
  }

  const handleRefresh = () => {
    loadRange(rangeKey)
  }

  const summaryUsd = rowCreditsTotal > 0 ? formatUsd(rowUsdTotal, rowCreditsTotal) : null

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-min-h-0">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-pt-6 tw-pb-4 tw-border-b tw-border-slate-100 tw-flex-shrink-0">
        <div>
          <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
            <History className="tw-h-5 tw-w-5 tw-text-slate-600" />
            <h1 className="tw-text-lg tw-font-semibold tw-text-slate-900">Tool Access</h1>
          </div>
          <p className="tw-text-sm tw-text-slate-500">
            One row per tool call; MCP sessions group many calls under one client.
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={status === 'loading'}
          className="tw-p-2 tw-rounded-lg tw-text-slate-400 hover:tw-text-slate-600 hover:tw-bg-slate-100 tw-transition-colors tw-border-0 tw-bg-transparent"
          title="Refresh"
        >
          <RefreshCw className={`tw-h-4 tw-w-4 ${status === 'loading' ? 'tw-animate-spin' : ''}`} />
        </button>
      </div>

      {/* Billing summary */}
      <div className="tw-flex-shrink-0 tw-px-6 tw-py-4 tw-border-b tw-border-slate-100 tw-bg-slate-50/80">
        <div className="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-4">
          <div>
            <div className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 tw-mb-1">
              Total of listed sessions
            </div>
            <div className="tw-text-2xl tw-font-semibold tw-tabular-nums tw-text-slate-900">
              {formatCredits(rowCreditsTotal)}
              <span className="tw-ml-1.5 tw-text-sm tw-font-medium tw-text-slate-500">credits</span>
              {summaryUsd ? (
                <span className="tw-ml-2 tw-text-base tw-font-semibold tw-text-slate-700">
                  · {summaryUsd}
                </span>
              ) : null}
            </div>
            <div className="tw-mt-1.5 tw-text-xs tw-text-slate-500">
              {sessions.length} session{sessions.length !== 1 ? 's' : ''} · {filtered.length} call
              {filtered.length !== 1 ? 's' : ''} · matches the rows below and updates with filters
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-overflow-hidden tw-text-xs tw-bg-white">
            {RANGE_OPTIONS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => loadRange(key)}
                disabled={status === 'loading'}
                className={`tw-px-3 tw-py-1.5 tw-font-medium tw-border-0 tw-transition-colors ${
                  rangeKey === key
                    ? 'tw-bg-slate-800 tw-text-white'
                    : 'tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="tw-flex tw-items-center tw-gap-3 tw-px-6 tw-py-3 tw-border-b tw-border-slate-100 tw-flex-shrink-0 tw-flex-wrap">
        <div className="tw-relative tw-flex-1 tw-min-w-[180px]">
          <Search className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-h-3.5 tw-w-3.5 tw-text-slate-400 tw-pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search tools or sessions…"
            className="tw-w-full tw-pl-8 tw-pr-8 tw-py-1.5 tw-text-sm tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-lg tw-outline-none focus:tw-border-blue-400 focus:tw-ring-1 focus:tw-ring-blue-100"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-p-0.5 tw-text-slate-400 hover:tw-text-slate-600 tw-border-0 tw-bg-transparent"
            >
              <X className="tw-h-3 tw-w-3" />
            </button>
          )}
        </div>

        <div className="tw-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-overflow-hidden tw-text-xs">
          {SOURCE_FILTER_OPTIONS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setSourceFilter(key)}
              className={`tw-px-3 tw-py-1.5 tw-font-medium tw-border-0 tw-transition-colors ${
                sourceFilter === key
                  ? 'tw-bg-slate-800 tw-text-white'
                  : 'tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50'
              }`}
              title={
                key === 'ai'
                  ? 'In-app AI chat tool calls'
                  : key === 'mcp'
                    ? 'External MCP clients (excludes scheduled tasks)'
                    : key === 'scheduled'
                      ? 'Scheduled task MCP tool calls'
                      : 'All sources'
              }
            >
              {label}
            </button>
          ))}
        </div>

        <div className="tw-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-overflow-hidden tw-text-xs">
          {(['all', 'read', 'write'] as WriteFilter[]).map(f => (
            <button
              key={f}
              type="button"
              onClick={() => setWriteFilter(f)}
              className={`tw-px-3 tw-py-1.5 tw-font-medium tw-border-0 tw-transition-colors ${
                writeFilter === f
                  ? 'tw-bg-slate-800 tw-text-white'
                  : 'tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        <div className="tw-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-overflow-hidden tw-text-xs">
          {(['all', 'completed', 'failed'] as StatusFilter[]).map(f => (
            <button
              key={f}
              type="button"
              onClick={() => setStatusFilter(f)}
              className={`tw-px-3 tw-py-1.5 tw-font-medium tw-border-0 tw-transition-colors ${
                statusFilter === f
                  ? 'tw-bg-slate-800 tw-text-white'
                  : 'tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {chatIdFilter && (
          <div className="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded-lg tw-bg-indigo-50 tw-border tw-border-indigo-200 tw-text-xs tw-text-indigo-700">
            <span>1 session</span>
            <button
              type="button"
              onClick={clearChatIdFilter}
              className="tw-p-0.5 tw-border-0 tw-bg-transparent tw-text-indigo-400 hover:tw-text-indigo-700"
              title="Clear session filter"
            >
              <X className="tw-h-3 tw-w-3" />
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto">
        {status === 'loading' && invocations.length === 0 ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-h-48 tw-text-slate-400 tw-text-sm">
            <RefreshCw className="tw-h-4 tw-w-4 tw-animate-spin tw-mr-2" />
            Loading tool access log…
          </div>
        ) : status === 'failed' ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-48 tw-text-slate-500 tw-gap-2">
            <AlertCircle className="tw-h-5 tw-w-5 tw-text-red-400" />
            <span className="tw-text-sm">Failed to load tool access log</span>
            <button
              type="button"
              onClick={handleRefresh}
              className="tw-text-xs tw-text-blue-600 hover:tw-underline tw-border-0 tw-bg-transparent tw-p-0"
            >
              Try again
            </button>
          </div>
        ) : sessions.length === 0 ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-48 tw-text-slate-400 tw-gap-1.5 tw-px-8 tw-text-center">
            <History className="tw-h-6 tw-w-6 tw-text-slate-300" />
            <span className="tw-text-sm tw-font-medium tw-text-slate-500">
              {invocations.length === 0 ? 'No tool invocations yet' : 'No results match your filters'}
            </span>
            {invocations.length === 0 && (
              <span className="tw-text-xs tw-text-slate-400 tw-max-w-xs">
                When your AI uses tools in chats or via MCP clients, they'll appear here.
              </span>
            )}
          </div>
        ) : (
          <table className="tw-w-full tw-text-sm">
            <thead className="tw-bg-slate-50 tw-border-b tw-border-slate-100 tw-sticky tw-top-0">
              <tr>
                <th className="tw-px-4 tw-py-2.5 tw-text-left tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide">
                  Session
                </th>
                <th className="tw-px-4 tw-py-2.5 tw-text-left tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide tw-whitespace-nowrap">
                  When
                </th>
                <th className="tw-px-4 tw-py-2.5 tw-text-left tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide">
                  Tool
                </th>
                <th className="tw-px-4 tw-py-2.5 tw-text-right tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide tw-whitespace-nowrap">
                  Credits / $
                </th>
                <th className="tw-px-4 tw-py-2.5 tw-text-left tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide tw-whitespace-nowrap">
                  Type
                </th>
                <th className="tw-px-4 tw-py-2.5 tw-text-left tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="tw-divide-y tw-divide-slate-50">
              {sessions.map(group => {
                const expanded = isExpanded(group)
                const { date, time } = formatTimestamp(group.latestTs)
                const displayCredits =
                  group.sessionCredits != null && group.sessionCredits > 0
                    ? group.sessionCredits
                    : group.creditsTotal
                const displayUsd =
                  group.sessionCredits != null && group.sessionCredits > 0
                    ? group.sessionUsd != null && group.sessionUsd > 0
                      ? group.sessionUsd
                      : group.sessionCredits / 2
                    : group.usdTotal
                const sessionUsd =
                  displayUsd > 0 ? formatUsd(displayUsd, displayCredits) : ''
                const creditsTitle = group.hasRunShareEstimates
                  ? 'Run or list-price estimate total. Expanded rows marked ~ are estimates, not separate per-tool bills.'
                  : group.creditsTotal <= 0 &&
                      group.sessionCredits != null &&
                      group.sessionCredits > 0
                    ? 'Billed on scheduled task / widget run (not per tool)'
                    : group.sessionCredits != null &&
                        group.sessionCredits > 0 &&
                        group.creditsTotal > 0 &&
                        group.sessionCredits !== group.creditsTotal
                      ? 'Session total from credit_usage (may exceed matched per-tool rows)'
                      : undefined
                const Chevron = expanded ? ChevronDown : ChevronRight
                return (
                  <React.Fragment key={group.chatId}>
                    <tr className="tw-bg-slate-50/60 hover:tw-bg-slate-100/80 tw-transition-colors">
                      <td className="tw-px-4 tw-py-2.5 tw-max-w-[280px]">
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-min-w-0">
                          <button
                            type="button"
                            onClick={() => toggleExpanded(group.chatId, expanded)}
                            className="tw-p-0.5 tw-border-0 tw-bg-transparent tw-text-slate-500 hover:tw-text-slate-800 tw-flex-shrink-0"
                            aria-expanded={expanded}
                            title={expanded ? 'Collapse' : 'Expand'}
                          >
                            <Chevron className="tw-h-4 tw-w-4" />
                          </button>
                          {group.fromMcp ? (
                            <div
                              className="tw-flex tw-items-center tw-gap-1.5 tw-min-w-0"
                              title={group.chatTitle || ''}
                            >
                              <span className="tw-truncate tw-text-xs tw-font-medium tw-text-slate-800">
                                {group.chatTitle?.substring(0, 40)}
                                {(group.chatTitle?.length ?? 0) > 40 ? '…' : ''}
                              </span>
                              <span className="tw-inline-flex tw-flex-shrink-0 tw-items-center tw-rounded-full tw-bg-violet-50 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-violet-700 tw-ring-1 tw-ring-violet-200">
                                MCP
                              </span>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleChatClick(group.chatId)}
                              className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-blue-600 hover:tw-text-blue-800 hover:tw-underline tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-max-w-full"
                              title={group.chatTitle || ''}
                            >
                              <span className="tw-truncate">
                                {group.chatTitle?.substring(0, 40)}
                                {(group.chatTitle?.length ?? 0) > 40 ? '…' : ''}
                              </span>
                              <ArrowUpRight className="tw-h-3 tw-w-3 tw-flex-shrink-0" />
                            </button>
                          )}
                          <span className="tw-flex-shrink-0 tw-text-xs tw-text-slate-500 tw-tabular-nums">
                            · {group.callCount} call{group.callCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </td>
                      <td className="tw-px-4 tw-py-2.5 tw-whitespace-nowrap">
                        <div className="tw-text-xs tw-font-medium tw-text-slate-700">{date}</div>
                        <div className="tw-text-xs tw-text-slate-400">{time}</div>
                      </td>
                      <td className="tw-px-4 tw-py-2.5 tw-text-xs tw-text-slate-500">
                        {expanded ? '' : 'Expand to see tools'}
                      </td>
                      <td
                        className="tw-px-4 tw-py-2.5 tw-whitespace-nowrap tw-text-right tw-text-xs tw-text-slate-700 tw-tabular-nums"
                        title={creditsTitle}
                      >
                        {displayCredits > 0 ? (
                          <div>
                            <div>{formatCredits(displayCredits)}</div>
                            {sessionUsd ? (
                              <div className="tw-text-slate-400">{sessionUsd}</div>
                            ) : null}
                            {group.hasRunShareEstimates ? (
                              <div className="tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-400">
                                {group.calls.every(
                                  (c) =>
                                    c.credits == null ||
                                    c.credits === 0 ||
                                    c.credits === 0.1 ||
                                    c.credits === 0.01 ||
                                    c.credits === 0.25
                                )
                                  ? 'est. list'
                                  : 'run total'}
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="tw-px-4 tw-py-2.5 tw-whitespace-nowrap">
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap">
                          {group.readCount > 0 && (
                            <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-slate-100 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-slate-600 tw-tabular-nums">
                              {group.readCount} Read
                            </span>
                          )}
                          {group.writeCount > 0 && (
                            <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-amber-50 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-amber-700 tw-ring-1 tw-ring-amber-200 tw-tabular-nums">
                              {group.writeCount} Write
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="tw-px-4 tw-py-2.5 tw-whitespace-nowrap">
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap">
                          {group.okCount > 0 && (
                            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-emerald-50 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-emerald-700 tw-ring-1 tw-ring-emerald-200 tw-tabular-nums">
                              <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-emerald-500" />
                              {group.okCount} OK
                            </span>
                          )}
                          {group.failedCount > 0 && (
                            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-red-50 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-red-700 tw-ring-1 tw-ring-red-200 tw-tabular-nums">
                              <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-red-500" />
                              {group.failedCount} Failed
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded &&
                      group.calls.map((inv, idx) => {
                        const ts = formatTimestamp(inv.timestamp)
                        const label = humanLabel(inv.tool)
                        const creditCell = formatCreditCell(inv)
                        return (
                          <tr
                            key={`${inv.chatId}-${inv.callIndex ?? inv.turn}-${inv.tool}-${idx}`}
                            className="hover:tw-bg-slate-50 tw-transition-colors"
                          >
                            <td className="tw-px-4 tw-py-2 tw-pl-12" />
                            <td className="tw-px-4 tw-py-2 tw-whitespace-nowrap">
                              <div className="tw-text-xs tw-font-medium tw-text-slate-700">
                                {ts.date}
                              </div>
                              <div className="tw-text-xs tw-text-slate-400">{ts.time}</div>
                            </td>
                            <td className="tw-px-4 tw-py-2">
                              <span className="tw-font-medium tw-text-slate-800">{label}</span>
                              <span className="tw-ml-1.5 tw-text-xs tw-text-slate-400 tw-font-mono">
                                {inv.tool}
                              </span>
                            </td>
                            <td
                              className="tw-px-4 tw-py-2 tw-whitespace-nowrap tw-text-right tw-text-xs tw-text-slate-700 tw-tabular-nums"
                              title={creditCell.title}
                            >
                              <div>{creditCell.text}</div>
                              {creditCell.usdText ? (
                                <div className="tw-text-slate-400">{creditCell.usdText}</div>
                              ) : null}
                              {creditCell.estimateLabel ? (
                                <div className="tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-400">
                                  {creditCell.estimateLabel}
                                </div>
                              ) : null}
                            </td>
                            <td className="tw-px-4 tw-py-2 tw-whitespace-nowrap">
                              {inv.isWrite ? (
                                <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-amber-50 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-amber-700 tw-ring-1 tw-ring-amber-200">
                                  Write
                                </span>
                              ) : (
                                <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-slate-100 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-slate-600">
                                  Read
                                </span>
                              )}
                            </td>
                            <td className="tw-px-4 tw-py-2 tw-whitespace-nowrap">
                              {inv.status === 'completed' ? (
                                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-emerald-50 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-emerald-700 tw-ring-1 tw-ring-emerald-200">
                                  <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-emerald-500" />
                                  OK
                                </span>
                              ) : (
                                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-red-50 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-red-700 tw-ring-1 tw-ring-red-200">
                                  <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-red-500" />
                                  Failed
                                </span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {filtered.length > 0 && (
        <div className="tw-flex-shrink-0 tw-border-t tw-border-slate-100 tw-px-6 tw-py-2 tw-text-xs tw-text-slate-400 tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1">
          <span>
            {sessions.length} session{sessions.length !== 1 ? 's' : ''} · {filtered.length} call
            {filtered.length !== 1 ? 's' : ''}
            {filtered.length !== invocations.length ? ` (${invocations.length} in period)` : ''}
          </span>
          <span>
            Total {formatCredits(rowCreditsTotal)} credits
            {summaryUsd ? ` (${summaryUsd})` : ''}
          </span>
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state: any) => ({
  currentApp: state.root.applicationId,
})

const mapDispatchToProps = {
  changeApplication,
}

export default connect(mapStateToProps, mapDispatchToProps)(ToolAccessMainViewComponent)
